<template>
  <footer class="bg-white py-8 md:py-16 xl:py-28">
    <DefaultGrid class="gap-y-10">
      <div
        v-if="props.cta"
        class="bg-mint col-span-full rounded-3xl px-4 pt-6 pb-14 md:px-6 md:pt-10 md:pb-16 lg:pt-16 lg:pb-20"
      >
        <div class="mx-auto max-w-2xl">
          <h2 class="sr-only">Join the movement</h2>
          <SvgJoinTheMovementLockup class="-mb-4" />
          <div
            class="flex flex-col items-center gap-y-1.5 md:flex-row md:justify-center md:gap-x-4 md:gap-y-3"
          >
            <ClientOnly>
              <PledgeLink
                location="footer"
                class="btn btn-lg btn-yellow h-[52px] text-[16px] md:h-[80px] md:text-[22px]"
              >
                <SvgGlyphPencil class="md:h-8 md:w-8" />
                <span>Join now</span>
              </PledgeLink>
            </ClientOnly>
            <NuxtLink
              to="/get-involved"
              class="btn btn-lg h-[52px] bg-white text-[16px] hover:bg-white md:h-[80px] md:text-[22px]"
            >
              <SvgGlyphSupport class="md:h-8 md:w-8" />
              <span>Get involved</span>
            </NuxtLink>
          </div>
        </div>
      </div>
      <div
        class="bg-grey col-span-full grid grid-cols-subgrid gap-y-10 rounded-3xl py-10 text-[18px]/[1.2em]"
      >
        <div
          class="col-span-full space-y-4 border-b border-black/10 px-6 pb-10 md:border-b-0 md:px-10 md:pb-0 lg:col-span-6"
        >
          <h4 class="text-[22px]/[1.2em] font-medium">
            Subscribe to the newsletter
          </h4>
          <BaseButton
            class="btn btn-sm btn-yellow"
            @click="uiStore.showNewsletterSignupModal = true"
          >
            <span>Sign up</span>
            <SvgGlyphArrowRight />
          </BaseButton>
        </div>
        <div
          class="col-span-full flex flex-col items-start gap-y-3 px-6 md:col-span-4 md:px-10 md:pr-0 lg:col-span-3"
        >
          <NuxtLink
            v-for="(item, idx) in globalsStore.settings?.menu?.items"
            :key="idx"
            class="transition-opacity hover:opacity-50"
            :to="useNavLinkUri(item)"
            >{{ item.label }}</NuxtLink
          >
        </div>
        <div
          class="col-span-full flex flex-col items-start gap-y-3 px-6 md:col-span-4 md:px-10 md:pl-0 lg:col-span-3"
        >
          <NuxtLink
            v-for="({ label, to }, idx) in secondaryNavLinks"
            :key="idx"
            class="transition-opacity hover:opacity-50"
            :to="to"
            >{{ label }}</NuxtLink
          >
        </div>
      </div>
      <div
        class="col-span-full flex flex-col gap-y-8 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex gap-x-5 md:items-center">
          <NuxtLink
            class="inline-block flex-none overflow-hidden rounded-full"
            to="https://www.acnc.gov.au/charity/charities/2e3187a1-5221-ef11-840b-00224893b304/profile"
            target="_blank"
          >
            <SvgAcncLogo class="h-auto w-10" />
          </NuxtLink>
          <div class="flex flex-col text-[14px]/[1.2em]">
            <span> &copy;{{ currentYear }} Wait Mate Foundation </span>
            <span class="text-black/50"
              >Website by
              <NuxtLink
                to="https://www.madetogether.com.au"
                target="_blank"
                class="transition-colors hover:text-black"
                >Made Together</NuxtLink
              ></span
            >
          </div>
        </div>
        <div class="flex gap-x-2.5">
          <SocialButton to="https://www.instagram.com/wait.mate/">
            <SvgGlyphInstagram />
          </SocialButton>
          <SocialButton
            to="https://www.facebook.com/people/Wait-Mate/61559910075995/"
          >
            <SvgGlyphFacebook />
          </SocialButton>
          <SocialButton to="https://www.linkedin.com/company/wait-mate">
            <SvgGlyphLinkedIn />
          </SocialButton>
        </div>
      </div>
    </DefaultGrid>
  </footer>
</template>

<script setup lang="ts">
interface Props {
  cta?: boolean
}

const props = defineProps<Props>()

const uiStore = useUIStore()
const globalsStore = useGlobalsStore()

const secondaryNavLinks = [
  {
    label: 'Get involved',
    to: '/get-involved',
  },
  {
    label: 'Email Us',
    to: 'mailto:hi@waitmate.org.au',
  },
  {
    label: 'Privacy',
    to: '/privacy',
  },
  {
    label: 'Terms',
    to: '/terms',
  },
  {
    label: 'Manage your commitment',
    to: '/manage-commitment',
  },
]

const currentYear = new Date().getFullYear()
</script>
